/**
 * @file OrderReturnsTab.tsx
 * @description Вкладка «Возвраты» заказа (phase10; phase19.31 — карточка
 * возврата отдельным компонентом, причина отказа — в окне кабинета
 * `ReasonDialog` вместо `window.prompt`).
 */

import { RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";

import { useHasPermission } from "../../auth/hooks";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { useNotify } from "../ui/NotifyProvider";
import { OrderReturnCard, type ReturnStep } from "./OrderReturnCard";
import { ReasonDialog } from "./ReasonDialog";
import { useFocusedRow } from "../../hooks/useFocusedRow";
import { useOrderTabRevisit } from "../../hooks/useOrderTabRevisit";
import { ApiError } from "../../lib/api-client";
import { listReturns, returnAction, type OrderDto, type ReturnDto } from "../../lib/api-orders";
import { OrderTabSkeleton } from "./OrderTabSkeleton";

/** Шаги без причины — вызов сервера по реестру; отказ идёт через окно причины. */
const STEP_CALL: Record<Exclude<ReturnStep, "reject">, (id: string) => Promise<ReturnDto>> = {
  approve: returnAction.approve,
  inspect: returnAction.inspect,
  approveRefund: returnAction.approveRefund,
  complete: returnAction.complete,
};

interface Props {
  order: OrderDto;
  canManage: boolean;
  onChanged?: () => void;
  /** id возврата из `?focus=` — переход из узла «Возврат» (phase19.24 §4). */
  focusId?: string | null;
  /** Вкладка сейчас открыта: при возврате на неё список дочитывается тихо. */
  active?: boolean;
}

export function OrderReturnsTab({ order, canManage, onChanged, focusId, active = true }: Props) {
  const notify = useNotify();
  const focused = useFocusedRow(focusId);
  const canProcess = useHasPermission("SHOP_PROCESS_RETURNS") || canManage;
  const [items, setItems] = useState<ReturnDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  // Возврат, по которому открыто окно отказа.
  const [rejecting, setRejecting] = useState<ReturnDto | null>(null);

  async function refresh() {
    try {
      setItems(await listReturns(order.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить возвраты");
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);
  useOrderTabRevisit(active, refresh);

  /** Выполнить шаг; `true` — получилось. */
  async function run(call: () => Promise<unknown>): Promise<boolean> {
    setActing(true);
    try {
      await call();
      notify.success("Действие выполнено");
      void refresh();
      onChanged?.();
      return true;
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось выполнить действие");
      return false;
    } finally {
      setActing(false);
    }
  }

  function onStep(item: ReturnDto, step: ReturnStep) {
    if (step === "reject") {
      setRejecting(item);
      return;
    }
    void run(() => STEP_CALL[step](item.id));
  }

  if (error) return <Alert severity="error">{error}</Alert>;
  if (items === null) {
    return (
      // Заготовка высотой блока, а не спиннер: страница не прыгает (qa4 п. 14).
      <OrderTabSkeleton />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {canManage ? (
        <div className="flex justify-end">
          <Button variant="outline" disabled>
            <RotateCcw className="size-4" />
            Оформить возврат (упрощённо)
          </Button>
        </div>
      ) : null}

      {items.length === 0 ? (
        <EditorialCard>
          <EditorialCard.Body>
            <p className="type-body py-8 text-center text-on-surface-variant">Возвратов нет.</p>
          </EditorialCard.Body>
        </EditorialCard>
      ) : (
        items.map((r) => (
          <OrderReturnCard
            key={r.id}
            item={r}
            currency={order.currency}
            rowAttr={focused.rowAttr(r.id)}
            highlighted={focused.highlightedId === r.id}
            canProcess={canProcess}
            acting={acting}
            onStep={(step) => onStep(r, step)}
          />
        ))
      )}

      <ReasonDialog
        open={rejecting !== null}
        busy={acting}
        danger
        onClose={() => setRejecting(null)}
        onConfirm={(reason) => (rejecting ? run(() => returnAction.reject(rejecting.id, reason)) : Promise.resolve(false))}
        texts={{
          title: rejecting?.number ? `Отказать по возврату ${rejecting.number}?` : "Отказать по возврату?",
          description: "Возврат получит статус «Отклонён». Причина сохранится в карточке возврата.",
          label: "Причина отказа",
          placeholder: "Срок возврата истёк, товар был в употреблении…",
          confirm: "Отказать",
          cancel: "Не отказывать",
        }}
      />
    </div>
  );
}
